<script lang="ts" setup>
import { defineAsyncComponent, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { DateTime } from 'luxon'
import { useAxios } from '@/composables/useAxios'
import { handleAxiosError, getErrorMessage } from '@/composables/useAxiosError'
import { uuid } from '@/composables'
import { useNotifyStore } from '@/stores'
import { PaginatedOauthGrants, type OauthGrantDetails } from '@/types'
import { groupScopes, type OauthScopeEntry, type OauthScopeGroup } from '@/utils/oauthScopes'
import { currencyLocale, formatCurrency } from '@/utils/formatCurrency'
import BaseDeleteModal from '@/components/base/BaseDeleteModal.vue'

const GrantActivity = defineAsyncComponent(() => import('./sections/GrantActivity.vue'))

const { t, locale } = useI18n()
const notifyStore = useNotifyStore()

const grants = ref<OauthGrantDetails[]>([])
const total = ref(0)
const loading = ref(true)
const loadFailed = ref(false)
const openActivity = ref<number | null>(null)
const toDisconnect = ref<OauthGrantDetails | null>(null)
const disconnecting = ref(false)

const load = async (): Promise<void> => {
  loading.value = true
  loadFailed.value = false
  try {
    const res = await useAxios('GET', '/account/grants?limit=200', true)
    const parsed = PaginatedOauthGrants.safeParse(res.data)
    if (!parsed.success) {
      loadFailed.value = true
      return
    }
    grants.value = parsed.data.data
    total.value = parsed.data.total
  } catch (error) {
    handleAxiosError(error, { logPrefix: 'AccountConnections' })
    loadFailed.value = true
  } finally {
    loading.value = false
  }
}

const disconnect = async (): Promise<void> => {
  const grant = toDisconnect.value
  if (!grant) return
  disconnecting.value = true
  try {
    await useAxios('DELETE', `/account/grants/${grant.id}`, true)
    notifyStore.createNotify({
      id: uuid('v4'),
      alertType: 'success',
      title: t('dashboard.account.connections.disconnect.success'),
      message: grant.client.name
    })
    toDisconnect.value = null
    await load()
  } catch (error) {
    handleAxiosError(error, { logPrefix: 'AccountConnections' })
    notifyStore.createNotify({
      id: uuid('v4'),
      alertType: 'error',
      title: t('dashboard.account.connections.disconnect.failed'),
      message: getErrorMessage(error)
    })
  } finally {
    disconnecting.value = false
  }
}

const toggleActivity = (id: number): void => {
  openActivity.value = openActivity.value === id ? null : id
}

const formatDate = (iso: string): string =>
  DateTime.fromISO(iso).setLocale(locale.value).toLocaleString(DateTime.DATETIME_MED)

const formatMoney = (cents: number, currency: string): string =>
  formatCurrency(cents / 100, currency, currencyLocale(locale.value))

const areaLabel = (group: OauthScopeGroup): string =>
  group.known ? t(`oauth.consent.areas.${group.area}`) : group.area

const levelLabel = (entry: OauthScopeEntry): string =>
  entry.known ? t(`oauth.consent.levels.${entry.level}`) : entry.scope

const statusClass = (status: string): string =>
  status === 'active' ? 'status-success' : 'status-grey'

// The schema reads a reason this build does not know as none.
const revokedReason = (reason: OauthGrantDetails['revoked_reason']): string =>
  t(`dashboard.account.connections.revokedReasons.${reason ?? 'other'}`)

onMounted(load)
</script>

<template>
  <div class="connections-page">
    <div class="page-header">
      <div class="header-icon">
        <v-icon icon="mdi-robot-outline" size="20" />
      </div>
      <div class="header-text">
        <h2 class="page-title">{{ $t('dashboard.account.connections.title') }}</h2>
        <p class="page-subtitle">{{ $t('dashboard.account.connections.subtitle') }}</p>
      </div>
    </div>

    <div v-if="loading" class="state-box">
      <v-progress-circular indeterminate size="32" width="3" color="#00B0FF" />
    </div>

    <div v-else-if="loadFailed" class="state-box" data-test="load-failed">
      <v-icon icon="mdi-alert-circle-outline" size="48" class="empty-icon" />
      <p class="empty-title">{{ $t('dashboard.account.connections.loadFailed') }}</p>
      <button class="btn-text" @click="load">{{ $t('dashboard.account.connections.retry') }}</button>
    </div>

    <div v-else-if="grants.length === 0" class="state-box" data-test="empty">
      <v-icon icon="mdi-robot-off-outline" size="48" class="empty-icon" />
      <p class="empty-title">{{ $t('dashboard.account.connections.empty.title') }}</p>
      <p class="empty-hint">{{ $t('dashboard.account.connections.empty.hint') }}</p>
    </div>

    <div v-else class="grant-list">
      <p v-if="total > grants.length" class="muted" data-test="grants-truncated">
        {{ $t('dashboard.account.connections.truncated', { shown: grants.length, total }) }}
      </p>
      <div
        v-for="grant in grants"
        :key="grant.id"
        class="section-card"
        :class="{ ended: grant.status !== 'active' }"
        data-test="grant"
      >
        <div class="grant-head">
          <div class="grant-identity">
            <span class="grant-name">{{ grant.client.name }}</span>
            <span v-if="grant.client.host" class="grant-host mono">{{ grant.client.host }}</span>
            <span v-else class="grant-host muted">{{ $t('dashboard.account.connections.noVerifiedHost') }}</span>
          </div>
          <span class="status-chip" :class="statusClass(grant.status)">
            {{ $t(`dashboard.account.connections.status.${grant.status}`) }}
          </span>
        </div>

        <div class="grant-scopes">
          <p v-if="grant.scopes.length === 0" class="muted">{{ $t('dashboard.account.connections.noScopes') }}</p>
          <div v-for="group in groupScopes(grant.scopes)" :key="group.area" class="scope-group">
            <span class="scope-area">{{ areaLabel(group) }}</span>
            <span
              v-for="entry in group.scopes"
              :key="entry.scope"
              class="scope-level"
              :class="`risk-${entry.risk}`"
            >{{ levelLabel(entry) }}</span>
          </div>
        </div>

        <div class="grant-facts">
          <div class="kv-row">
            <span class="kv-label">{{ $t('dashboard.account.connections.spend') }}</span>
            <span v-if="grant.spend_cap_cents > 0">
              {{ $t('dashboard.account.connections.spendOf', {
                spent: formatMoney(grant.spent_cents, grant.currency),
                cap: formatMoney(grant.spend_cap_cents, grant.currency)
              }) }}
            </span>
            <span v-else>{{ $t('dashboard.account.connections.noSpending') }}</span>
          </div>
          <div class="kv-row">
            <span class="kv-label">{{ $t('dashboard.account.connections.connected') }}</span>
            <span>{{ $t('dashboard.account.connections.connectedFrom', { date: formatDate(grant.consented_at), ip: grant.consent_ip }) }}</span>
          </div>
          <div v-if="grant.transfer_consent" class="kv-row" data-test="transfer-consent">
            <span class="kv-label">{{ $t('dashboard.account.connections.transferConsent') }}</span>
            <span>{{ $t('dashboard.account.connections.transferConsentGiven') }}</span>
          </div>
          <div class="kv-row">
            <span class="kv-label">{{ $t('dashboard.account.connections.lastPresented') }}</span>
            <span>{{ grant.last_used_at ? formatDate(grant.last_used_at) : $t('dashboard.account.connections.never') }}</span>
          </div>
          <div v-if="grant.status === 'active' || grant.expires_at" class="kv-row">
            <span class="kv-label">{{ $t('dashboard.account.connections.expires') }}</span>
            <span>{{ grant.expires_at ? formatDate(grant.expires_at) : $t('dashboard.account.connections.noExpiry') }}</span>
          </div>
          <div v-if="grant.revoked_at" class="kv-row">
            <span class="kv-label">{{ $t('dashboard.account.connections.ended') }}</span>
            <span>{{ formatDate(grant.revoked_at) }}, {{ revokedReason(grant.revoked_reason) }}</span>
          </div>
        </div>

        <div class="grant-actions">
          <button class="btn-text" data-test="activity" @click="toggleActivity(grant.id)">
            {{ openActivity === grant.id ? $t('dashboard.account.connections.hideActivity') : $t('dashboard.account.connections.showActivity') }}
          </button>
          <button
            v-if="grant.status === 'active'"
            class="btn-text btn-danger"
            data-test="disconnect"
            @click="toDisconnect = grant"
          >
            {{ $t('dashboard.account.connections.disconnect.action') }}
          </button>
        </div>

        <GrantActivity v-if="openActivity === grant.id" :grant-id="grant.id" />
      </div>
    </div>

    <BaseDeleteModal
      v-if="toDisconnect"
      show
      :title="$t('dashboard.account.connections.disconnect.title')"
      :subtitle="toDisconnect.client.name"
      :confirm-text="$t('dashboard.account.connections.disconnect.message')"
      :warning-text="$t('dashboard.account.connections.disconnect.warning')"
      :loading="disconnecting"
      :delete-button-text="$t('dashboard.account.connections.disconnect.confirm')"
      :deleting-button-text="$t('dashboard.account.connections.disconnect.working')"
      @delete="disconnect"
      @close="toDisconnect = null"
    />
  </div>
</template>

<style lang="scss" scoped>
.connections-page {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.page-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding-bottom: 16px;
  border-bottom: 1px solid rgba(120, 163, 255, 0.08);
}

.header-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: rgba(0, 176, 255, 0.1);
  border: 1px solid rgba(0, 176, 255, 0.2);
  border-radius: 10px;
  color: #4fc3f7;
}

.page-title {
  font-size: 1rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
  margin: 0 0 2px;
}

.page-subtitle {
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.5);
  margin: 0;
}

.state-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 48px 24px;
  text-align: center;
}

.empty-icon {
  color: rgba(200, 218, 255, 0.25);
}

.empty-title {
  font-size: 1rem;
  font-weight: 500;
  color: rgba(200, 218, 255, 0.6);
  margin: 0;
}

.empty-hint {
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.4);
  margin: 0;
  max-width: 520px;
}

.grant-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.section-card {
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(120, 163, 255, 0.08);
  border-radius: 16px;
  padding: 24px;

  &.ended {
    opacity: 0.7;
  }
}

.grant-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
}

.grant-identity {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.grant-name {
  font-size: 1.1rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  overflow-wrap: anywhere;
}

.grant-host {
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.8);
}

.mono {
  font-family: 'JetBrains Mono', 'Fira Code', monospace;
  font-weight: 500;
}

.muted {
  color: rgba(200, 218, 255, 0.5);
  font-size: 0.85rem;
  margin: 0;
}

.status-chip {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 0.78rem;
  font-weight: 500;
  white-space: nowrap;
}

.status-success {
  background: rgba(129, 199, 132, 0.1);
  border: 1px solid rgba(129, 199, 132, 0.3);
  color: #81c784;
}

.status-grey {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: rgba(200, 218, 255, 0.5);
}

.grant-scopes {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 16px;
}

.scope-group {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
}

.scope-area {
  min-width: 140px;
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.8);
}

.scope-level {
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 0.78rem;
  font-weight: 500;

  &.risk-low {
    background: rgba(0, 176, 255, 0.1);
    border: 1px solid rgba(0, 176, 255, 0.3);
    color: #4fc3f7;
  }

  &.risk-medium {
    background: rgba(255, 183, 77, 0.1);
    border: 1px solid rgba(255, 183, 77, 0.3);
    color: #ffb74d;
  }

  &.risk-high {
    background: rgba(244, 67, 54, 0.1);
    border: 1px solid rgba(244, 67, 54, 0.3);
    color: #ef5350;
  }
}

.grant-facts {
  border-top: 1px solid rgba(120, 163, 255, 0.08);
  margin-bottom: 16px;
}

.kv-row {
  display: grid;
  grid-template-columns: 200px 1fr;
  gap: 12px;
  padding: 10px 12px;
  border-bottom: 1px solid rgba(120, 163, 255, 0.05);
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.85);

  &:last-child {
    border-bottom: none;
  }
}

.kv-label {
  color: rgba(200, 218, 255, 0.5);
  font-size: 0.85rem;
}

.grant-actions {
  display: flex;
  gap: 10px;
}

.btn-text {
  padding: 6px 12px;
  background: rgba(120, 163, 255, 0.08);
  border: 1px solid rgba(120, 163, 255, 0.15);
  border-radius: 8px;
  color: rgba(200, 218, 255, 0.8);
  font-size: 0.82rem;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    background: rgba(120, 163, 255, 0.15);
  }
}

.btn-danger {
  background: rgba(244, 67, 54, 0.08);
  border-color: rgba(244, 67, 54, 0.2);
  color: #ef5350;

  &:hover {
    background: rgba(244, 67, 54, 0.15);
  }
}

@media (max-width: 600px) {
  .kv-row {
    grid-template-columns: 1fr;
    gap: 2px;
  }

  .scope-area {
    min-width: 100%;
  }
}
</style>
